import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function HowToOrderPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: 'How to Order Research Peptides | DN Biotech Quote-Based Process',
    description: 'Learn DN Biotech\'s quote-based ordering process for research peptides. B2B supplier with manual review, COA documentation, and door-to-door delivery for laboratories.',
    keywords: 'how to order research peptides, peptide ordering process, quote-based ordering, B2B peptide supplier, laboratory ordering',
    canonical: `${siteUrl}/how-to-order`,
    schema: generateWebPageSchema(siteUrl, '/how-to-order', 'How to Order Research Peptides | DN Biotech Quote-Based Process', 'Learn DN Biotech\'s quote-based ordering process for research peptides. B2B supplier with manual review, COA documentation, and door-to-door delivery for laboratories.')
  });

  const orderSteps = [
    {
      number: '01',
      title: 'Browse the Catalog',
      description: 'Explore our comprehensive catalog of research peptides and biotech compounds. View available strengths, vial sizes, packaging formats, and specifications for each product. All items are listed for reference and inquiry purposes only.',
      icon: 'ri-search-line',
      details: [
        'View product specifications and purity levels',
        'Check available packaging formats (bottled/boxed)',
        'Review Certificate of Analysis (COA) availability',
        'Compare different compound options for your research'
      ]
    },
    {
      number: '02',
      title: 'Submit a Quote Request',
      description: 'Contact us to request pricing and availability. Use our Product Inquiry form on any product page, or reach out directly via WhatsApp. No direct checkout or automated purchasing is available on this website.',
      icon: 'ri-file-list-3-line',
      details: [
        'Complete the Product Inquiry form with your requirements',
        'Include quantity needed and any specific requests',
        'Provide your institution or organization details',
        'Alternative: Contact us directly via WhatsApp for faster response'
      ]
    },
    {
      number: '03',
      title: 'Review & Approval',
      description: 'Our team manually reviews each inquiry to ensure compliance and accuracy. This verification process is essential for maintaining our standards as a B2B research supplier.',
      icon: 'ri-shield-check-line',
      details: [
        'Verification of product availability and batch status',
        'Confirmation of order details and specifications',
        'Research-use compliance verification',
        'Assessment of shipping requirements to your location'
      ]
    },
    {
      number: '04',
      title: 'Receive Your Quote',
      description: 'After review, you will receive a detailed quote including pricing, lead time, shipping method, and available payment options. Quotes are typically provided within 24-48 business hours.',
      icon: 'ri-mail-check-line',
      details: [
        'Itemized pricing for requested products',
        'Lead time: typically 5-10 business days',
        'Shipping method: Door-to-door via FedEx/UPS',
        'Available payment methods listed'
      ]
    },
    {
      number: '05',
      title: 'Payment & Processing',
      description: 'Once you approve the quote, complete payment using one of our accepted methods. Your order is then processed, packaged in pharmaceutical-grade containers, and shipped with tracking.',
      icon: 'ri-secure-payment-line',
      details: [
        'Payment processed only after quote approval',
        'Accepted: BTC, ETH, USDT, Credit Card, PayPal',
        'Secure packaging: bottled/boxed with proper sealing',
        'Door-to-door delivery with double customs clearance'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div 
            className="inline-flex items-center space-x-2 px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6"
            aria-label="DN Bio Research"
          >
            <svg 
              className="w-5 h-5 text-primary-400" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="1.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M12 3v18" />
              <path d="M6 6c0 3.5 2.5 6 6 6s6-2.5 6-6" />
              <path d="M6 18c0-3.5 2.5-6 6-6s6 2.5 6 6" />
              <circle cx="6" cy="6" r="1.5" fill="currentColor" />
              <circle cx="18" cy="6" r="1.5" fill="currentColor" />
              <circle cx="6" cy="18" r="1.5" fill="currentColor" />
              <circle cx="18" cy="18" r="1.5" fill="currentColor" />
              <circle cx="12" cy="12" r="1.5" fill="currentColor" />
            </svg>
            <span className="text-white font-semibold tracking-wide text-sm">B2B Research Supplier</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-6">
            How to Order
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto">
            DNBio operates as a B2B research supplier with a manual, quote-based ordering process. All products are supplied exclusively for laboratory and research use.
          </p>
        </div>
      </section>

      {/* Research Use Only Banner */}
      <section className="py-6 bg-red-50 border-b border-red-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 flex items-center justify-center bg-red-500 rounded-lg flex-shrink-0">
              <i className="ri-alert-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-red-900 mb-1">For Research Use Only</h3>
              <p className="text-red-800 leading-relaxed">
                <strong>Not for human consumption.</strong> No medical, therapeutic, veterinary, or diagnostic use. All products are intended solely for laboratory research purposes by qualified researchers and institutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote-Based System Explanation */}
      <section className="py-16 bg-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">Quote-Based Ordering System</h2>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Unlike consumer e-commerce stores, DNBio does not offer direct checkout or automated purchasing. Every order requires manual review and quote approval.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-store-3-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Not a Retail Store</h3>
              <p className="text-gray-600 text-sm">
                This website functions as a product catalog and inquiry system, not a consumer pharmacy or supplement store.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-file-text-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Manual Quote Required</h3>
              <p className="text-gray-600 text-sm">
                All orders require a personalized quote. Pricing and availability are confirmed manually for each inquiry.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
                <i className="ri-shield-check-line text-2xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Compliance Verified</h3>
              <p className="text-gray-600 text-sm">
                Each request is reviewed to ensure research-use compliance before any order is processed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5-Step Order Process */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-navy-900 mb-4">5-Step Ordering Process</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Follow these steps to request a quote and place your research compound order with DNBio
            </p>
          </div>

          <div className="space-y-8">
            {orderSteps.map((step, index) => (
              <div key={index} className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
                <div className="flex flex-col lg:flex-row">
                  {/* Step Number & Icon */}
                  <div className="lg:w-48 bg-gradient-to-br from-primary-500 to-primary-600 p-8 flex flex-col items-center justify-center text-white">
                    <span className="text-4xl font-bold mb-2">{step.number}</span>
                    <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-xl">
                      <i className={`${step.icon} text-3xl`}></i>
                    </div>
                  </div>

                  {/* Step Content */}
                  <div className="flex-1 p-8">
                    <h3 className="text-2xl font-bold text-navy-900 mb-3">{step.title}</h3>
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {step.description}
                    </p>
                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start text-sm text-gray-600">
                          <i className="ri-check-line text-primary-500 mr-2 mt-0.5 flex-shrink-0"></i>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Order Details Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-navy-900 mb-12 text-center">Order Information</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* MOQ */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-box-3-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Minimum Order</h3>
              <p className="text-gray-700 mb-2 font-medium">MOQ: 1 box</p>
              <p className="text-sm text-gray-600">
                We accommodate both small-scale research projects and bulk institutional orders
              </p>
            </div>

            {/* Lead Time */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-time-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Lead Time</h3>
              <p className="text-gray-700 mb-2 font-medium">5-10 business days</p>
              <p className="text-sm text-gray-600">
                From order confirmation to shipment dispatch
              </p>
            </div>

            {/* Shipping */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-truck-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Shipping Method</h3>
              <p className="text-gray-700 mb-2 font-medium">Door-to-door delivery</p>
              <p className="text-sm text-gray-600">
                FedEx / UPS with double customs clearance
              </p>
            </div>

            {/* Packaging */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-archive-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Packaging</h3>
              <p className="text-gray-700 mb-2 font-medium">Bottled / Boxed</p>
              <p className="text-sm text-gray-600">
                Secure pharmaceutical-grade packaging with proper sealing and temperature control
              </p>
            </div>

            {/* Payment Methods */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-secure-payment-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Payment Options</h3>
              <p className="text-xs text-gray-500 mb-3 italic">After quote approval only</p>
              <div className="space-y-1 text-gray-700 text-sm">
                <p>• BTC, ETH, USDT (Cryptocurrency)</p>
                <p>• Credit Card</p>
                <p>• PayPal</p>
              </div>
            </div>

            {/* COA */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                <i className="ri-file-list-3-line text-2xl text-primary-600"></i>
              </div>
              <h3 className="text-lg font-bold text-navy-900 mb-2">Documentation</h3>
              <p className="text-gray-700 mb-2 font-medium">COA Included</p>
              <p className="text-sm text-gray-600">
                Certificate of Analysis with HPLC verification for every order
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance Notice Section */}
      <section className="py-16 bg-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="w-16 h-16 flex items-center justify-center bg-red-500 rounded-2xl mx-auto mb-4">
              <i className="ri-shield-star-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-red-900 mb-4">Important Compliance Notice</h2>
            <p className="text-lg text-red-800">
              Please read and understand the following before submitting any inquiry
            </p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-md border border-red-200">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-flask-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Research Use Only</h3>
                  <p className="text-red-800 text-sm leading-relaxed">
                    All products sold by DNBio are intended <strong>strictly for laboratory research and scientific study</strong>. Products are supplied to qualified researchers, academic institutions, and R&D facilities only.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-close-circle-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Not for Human or Veterinary Use</h3>
                  <p className="text-red-800 text-sm leading-relaxed">
                    No products are intended for human consumption, veterinary use, medical treatment, therapeutic application, or diagnostic purposes. <strong>Not for injection, ingestion, or any form of administration to living organisms.</strong>
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-hospital-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">No Medical Information Provided</h3>
                  <p className="text-red-800 text-sm leading-relaxed">
                    DNBio does not provide medical advice, dosing instructions, treatment protocols, or health-related guidance. No claims are made regarding the treatment, cure, or prevention of any disease or medical condition.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-building-2-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">B2B Research Supplier</h3>
                  <p className="text-red-800 text-sm leading-relaxed">
                    This website operates as a <strong>B2B research supplier catalog and inquiry system</strong>. We are not a pharmacy, supplement store, or consumer retail operation. All transactions are conducted on a quote-basis with manual approval.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0 mt-0.5">
                  <i className="ri-user-settings-line text-red-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-red-900 mb-1">Buyer Responsibility</h3>
                  <p className="text-red-800 text-sm leading-relaxed">
                    By submitting an inquiry, you confirm that you are a qualified researcher or represent a legitimate research institution, and that you will use products in accordance with all applicable laws, regulations, and safety protocols.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-navy-900 to-navy-800 rounded-2xl p-12 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Request a Quote?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Contact DNBio today for personalized pricing and availability on research-grade peptides and compounds
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://wa.me/639178850200"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-green-500 text-white text-lg font-semibold rounded-lg hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2 text-xl"></i>
                Chat on WhatsApp
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-navy-900 text-lg font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                Submit Inquiry
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
            <p className="text-sm text-gray-400 mt-6">
              All inquiries are reviewed within 24-48 business hours
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
